import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import { createConfigParamTypeExistingColumnList, ETLDryRunObjectTableAddColumn } from '../utils';
import {
  addDryRunObjectTable,
  getETLComponentInputObjectTable,
  getETLTableObjectColumn,
  getETLTableObjectColumnDataTypeIfPresent,
  getETLTableObjectColumnOptional,
} from '../../utils';
import { get_guid } from '../../../visual-analytics/helpers/misc';

import styles from '../../edit-etl-component/editETLComponentForm.module.scss';
import {
  aggregationHasCompatibleSourceColumn,
  aggregationTypesList,
  defaultAggregationType,
  getAggregationTypeInfo,
} from '../../aggregations/aggregationTypes';
import { addETLDryRunError, getDryRunErrorsForComponent } from '../../etlDryRun';
import ParamSet from '../../edit-etl-component/ParamSet';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { TpAggregationInstance } from '../../aggregations/interfaces';

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const inputTableObj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);

  try {
    const colIdList = (comp.params['groupByColumns'] || []) as string[];
    if (colIdList.length == 0) throw `Group by column(s) not specified`;
    for (const colId of colIdList) {
      const sourceColInfo = getETLTableObjectColumn(inputTableObj, colId);
      ETLDryRunObjectTableAddColumn(
        outputTableObj,
        sourceColInfo.identifier,
        sourceColInfo.name,
        sourceColInfo.description,
        sourceColInfo.dataType,
      );
    }
  } catch (error) {
    addETLDryRunError(dryRun, String(error), comp.id, 'groupByColumns');
  }

  for (const agg of (comp.params['aggregations'] || []) as TpAggregationInstance[]) {
    try {
      if (!agg.sourceColId) throw 'Aggregation source column not specified';
      const aggTypeInfo = getAggregationTypeInfo(agg.aggType);
      const sourceColInfo = getETLTableObjectColumn(inputTableObj, agg.sourceColId);
      if (!agg.destColId) addETLDryRunError(dryRun, `Aggregation output column has no identifier`, comp.id, agg.id);

      if (aggTypeInfo.performDryRun) {
        aggTypeInfo.performDryRun({ dryRun, comp, agg });
      }
      if (agg.destColId) {
        ETLDryRunObjectTableAddColumn(
          outputTableObj,
          agg.destColId,
          agg.destColName || `${sourceColInfo.name} - ${aggTypeInfo.getAggName(agg)}`,
          agg.destColDescription || `${sourceColInfo.description} - ${aggTypeInfo.getAggName(agg)}`,
          aggTypeInfo.outputDataType || sourceColInfo.dataType,
        );
      }
    } catch (error) {
      addETLDryRunError(dryRun, String(error), comp.id, agg.id);
    }
  }

  return {
    abort: false,
  };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const sourceTable = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');

  if (sourceTable.columns.length == 0) return null;

  // Contains the list of aggregations to be performed. Each aggregation takes a source column and produces an output column
  const aggregations = (comp.params['aggregations'] || []) as TpAggregationInstance[];

  function updateAggregationParam(aggId: string, paramId: string, newValue: any) {
    const aggIdx = aggregations.map((agg) => agg.id).indexOf(aggId);
    if (aggIdx < 0) throw `Invalid aggegation id ${aggId}`;
    let aggregationTypeChanged = false;
    if (paramId == 'colId') {
      const sourceColId = newValue;
      aggregations[aggIdx].sourceColId = sourceColId;
      const sourceColInfo = getETLTableObjectColumnOptional(sourceTable, sourceColId);
      if (sourceColInfo) {
        const currentAggType = getAggregationTypeInfo(aggregations[aggIdx].aggType);
        if (!aggregationHasCompatibleSourceColumn(currentAggType.id, sourceColInfo.dataType)) {
          // New source column cannot be used for the current aggregation type => reset to default that works for every type of data
          aggregations[aggIdx].aggType = defaultAggregationType.id;
          aggregationTypeChanged = true;
        }
      }
    } else if (paramId == 'aggType') {
      aggregations[aggIdx].aggType = newValue;
      aggregationTypeChanged = true;
    } else if (paramId == 'destColId') aggregations[aggIdx].destColId = newValue;
    else if (paramId == 'destColName') aggregations[aggIdx].destColName = newValue;
    else if (paramId == 'destColDescription') aggregations[aggIdx].destColDescription = newValue;
    else aggregations[aggIdx].params[paramId] = newValue;

    if (aggregationTypeChanged) {
      // The aggregation type changed => we reset any custom parameters
      const aggTypeInfo = getAggregationTypeInfo(aggregations[aggIdx].aggType);
      aggregations[aggIdx].params = { ...aggTypeInfo.defaultCustomParams };
    }
    updateEditingComponent(comp);
  }

  function renderAggregationErrors(agg: TpAggregationInstance) {
    const errors = getDryRunErrorsForComponent(dryRun, comp.id).filter((error) => error.paramId == agg.id);
    if (errors.length == 0) return null;
    return (
      <div className={styles.errors}>
        {errors.map((error, idx) => (
          <div key={`errors_${agg.id}_${idx}`}>{error.message}</div>
        ))}
      </div>
    );
  }

  function renderCustomAggregationParams(agg: TpAggregationInstance) {
    const aggTypeInfo = getAggregationTypeInfo(agg.aggType);
    if (!aggTypeInfo.renderCustomParams) return null;
    return (
      <>
        <h3>Custom parameters</h3>
        {aggTypeInfo.renderCustomParams({
          agg,
          sourceTable,
          updateAggregationParam: (paramId: string, paramValue: any) => {
            updateAggregationParam(agg.id, paramId, paramValue);
          },
        })}
      </>
    );
  }

  return (
    <>
      {aggregations.map((agg) => (
        <div className={styles.section}>
          <h3>
            Aggregation
            <button
              onClick={() => {
                comp.params['aggregations'] = aggregations.filter((filtAgg) => agg.id !== filtAgg.id);
                updateEditingComponent(comp);
              }}
            >
              <FontAwesomeIcon icon="xmark" />
            </button>
          </h3>
          {renderAggregationErrors(agg)}

          <ParamSet
            params={[
              {
                name: 'Source column',
                component: (
                  <select
                    value={agg.sourceColId}
                    onChange={(ev) => {
                      updateAggregationParam(agg.id, 'colId', ev.target.value);
                    }}
                  >
                    <option key="_empty_" value={''}>
                      - Select -
                    </option>
                    {sourceTable.columns.map((col) => (
                      <option key={col.identifier} value={col.identifier}>
                        {col.name}
                      </option>
                    ))}
                  </select>
                ),
              },
              {
                name: 'Aggregation type',
                component: (
                  <>
                    <select
                      value={agg.aggType}
                      onChange={(ev) => {
                        updateAggregationParam(agg.id, 'aggType', ev.target.value);
                      }}
                    >
                      {aggregationTypesList
                        .filter((aggType) =>
                          aggregationHasCompatibleSourceColumn(
                            aggType.id,
                            getETLTableObjectColumnDataTypeIfPresent(sourceTable, agg.sourceColId),
                          ),
                        )
                        .map((aggType) => (
                          <option key={aggType.id} value={aggType.id}>
                            {aggType.name}
                          </option>
                        ))}
                    </select>
                    <div style={{ marginTop: '3px' }}>
                      {agg.aggType && getAggregationTypeInfo(agg.aggType).description}
                    </div>
                  </>
                ),
              },
              {
                name: 'Result column identifier',
                component: (
                  <input
                    value={agg.destColId}
                    onChange={(ev) => {
                      updateAggregationParam(agg.id, 'destColId', ev.target.value);
                    }}
                  />
                ),
              },
              {
                name: 'Result column name',
                component: (
                  <input
                    value={agg.destColName}
                    placeholder="Automatic"
                    onChange={(ev) => {
                      updateAggregationParam(agg.id, 'destColName', ev.target.value);
                    }}
                  />
                ),
              },
              {
                name: 'Result column description',
                component: (
                  <input
                    value={agg.destColDescription}
                    placeholder="Automatic"
                    onChange={(ev) => {
                      updateAggregationParam(agg.id, 'destColDescription', ev.target.value);
                    }}
                  />
                ),
              },
            ]}
          />

          {renderCustomAggregationParams(agg)}
        </div>
      ))}

      <FancyButton
        iconName="fas:plus"
        onClick={() => {
          const newAgg: TpAggregationInstance = {
            id: get_guid(),
            sourceColId: sourceTable.columns[0].identifier,
            aggType: defaultAggregationType.id,
            destColId: '',
            destColName: '',
            destColDescription: '',
            params: {},
          };
          comp.params['aggregations'] = [...aggregations, newAgg];
          updateEditingComponent(comp);
        }}
      >
        Add aggregation
      </FancyButton>
    </>
  );
}

export const manifestETLTableAggregate: TpETLComponentTypeManifest = {
  id: 'TableAggregate',
  name: 'Aggregate Table',
  description: 'Aggregate the rows in a table, combining all rows that have the same value for a specified column',
  categoryId: 'table',
  icon: <FontAwesomeIcon icon={['fas', 'arrows-up-to-line']} />,
  inputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'sourceTable',
      name: 'Source Table',
      columns: [],
    },
  ],
  outputs: [
    {
      id: 'result',
      name: 'Result table',
      type: TpETLObjectTypes.TABLE,
      description: 'Aggregated table',
      columns: [],
    },
  ],
  params: [
    {
      id: 'groupByColumns',
      name: 'Group by column(s)',
      description:
        'Column(s) used for grouping. In the result table, a separate record will be created for each distinct value of this column(s) in the source table.',
      paramType: createConfigParamTypeExistingColumnList('sourceTable'),
    },
  ],
  performDryRun,
  renderCustomConfigParams,
};
